import { icons } from '../icons';
import React from 'react';
import KoobPlaceholder from './KoobPlaceholder';
import KoobStat from './KoobStat';
import KoobTooltip from './KoobTooltip';

type KoobTripDesignerHeaderProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'className'
> & {
  /** Small uppercase kicker above the trip name (e.g. "Current trip:"). */
  tripLabel: React.ReactNode;
  tripName?: React.ReactNode;
  /** Renders a pencil button next to the trip name (e.g. open the update modal). */
  onTripEdit?: () => void;
  editLabel?: string;
  /** Slot for the app's version selector popover. */
  versionSelector?: React.ReactNode;
  /** Uppercase kicker next to the price (e.g. "From"). */
  priceLabel?: React.ReactNode;
  /** Formatted total price (e.g. "$2,016.54"). */
  price?: React.ReactNode;
  /** Formatted per-participant line (e.g. "$1,008.27 / Pax"). */
  pricePerPax?: React.ReactNode;
  priceLoading?: boolean;
  /** Replaces the price when set (e.g. currency mismatch message). */
  priceError?: React.ReactNode;
  /** Tooltip content for the info icon next to the price. */
  priceTip?: React.ReactNode;
  /** Right-aligned actions (chat, exit, locale switcher…). */
  actions?: React.ReactNode;
};

const KoobTripDesignerHeader = React.forwardRef<
  HTMLDivElement,
  KoobTripDesignerHeaderProps
>(
  (
    {
      tripLabel,
      tripName,
      onTripEdit,
      editLabel,
      versionSelector,
      priceLabel,
      price,
      pricePerPax,
      priceLoading = false,
      priceError,
      priceTip,
      actions,
      ...props
    },
    ref,
  ) => (
    <div
      ref={ref}
      className="flex w-full flex-wrap items-center justify-between gap-3"
      {...props}
    >
      <div className="flex min-w-0 flex-wrap items-stretch divide-x divide-y-0 divide-solid divide-border rounded-md border border-solid border-border bg-white">
        <div className="flex items-center gap-3 px-3 py-1.5">
          <KoobStat
            label={tripLabel}
            value={tripName}
            valueClassName="max-w-[14rem] truncate"
          />

          {onTripEdit && (
            <button
              type="button"
              onClick={onTripEdit}
              aria-label={editLabel}
              className="border-none bg-transparent p-0 text-muted-foreground transition-colors hover:text-brand cursor-pointer"
            >
              <i className="fa-light fa-pen" />
            </button>
          )}
        </div>

        {versionSelector && (
          <div className="flex items-center self-stretch">
            {versionSelector}
          </div>
        )}

        {(priceLoading || priceError || price) && (
          <div className="flex items-center gap-3 self-stretch px-3 py-1.5">
            {priceLoading ? (
              <div>
                <KoobPlaceholder className="h-4 w-28 rounded" />
                <KoobPlaceholder className="mt-1 h-3 w-20 rounded" />
              </div>
            ) : priceError ? (
              <div className="flex items-center space-x-2 leading-none">
                <i className={`${icons.warning} text-red-600`} />
                <div className="text-sm font-semibold text-gray-500">
                  {priceError}
                </div>
              </div>
            ) : (
              <KoobStat
                layout="row"
                label={priceLabel}
                value={price}
                subValue={pricePerPax}
                // id kept from the legacy navbar block — e2e hooks onto it
                valueProps={{ id: 'navbar-trip-designer-price' }}
              />
            )}

            {priceTip && (
              <div className="text-muted-foreground">
                <KoobTooltip tip={priceTip} side="bottom">
                  <i className="fa-solid fa-circle-info text-[13px]" />
                </KoobTooltip>
              </div>
            )}
          </div>
        )}
      </div>

      {actions && (
        <div className="flex flex-wrap items-center gap-3">{actions}</div>
      )}
    </div>
  ),
);

KoobTripDesignerHeader.displayName = 'KoobTripDesignerHeader';

export default KoobTripDesignerHeader;
